// CON-9505. A redirect-based payment method — Amazon Pay, or a card that needed a 3DS challenge — comes
// back to the session's return_url, which the server sets to /cart/?stripe_checkout_return=<session id>.
//
// Read straight off window.location rather than from any router state: this is consulted during the very
// first render to decide whether to create a session, and anything that only populates after hydration
// would be empty at that point — long enough for a second session to be created and the buyer's
// authorisation to be orphaned.
export const CHECKOUT_SESSION_RETURN_PARAM = 'stripe_checkout_return'

export const getCheckoutSessionReturnId = () => {
    if (typeof window === 'undefined') {
        return null
    }

    return new URLSearchParams(window.location.search).get(
        CHECKOUT_SESSION_RETURN_PARAM
    )
}

export const isCheckoutSessionReturn = () => Boolean(getCheckoutSessionReturnId())

// Drops the return parameter so the page stops treating this as a finished session. Used when the
// returned payment turns out not to be confirmed and the buyer needs the form back.
export const clearCheckoutSessionReturn = () => {
    if (typeof window === 'undefined' || !window.history?.replaceState) {
        return
    }

    const url = new URL(window.location.href)
    url.searchParams.delete(CHECKOUT_SESSION_RETURN_PARAM)
    window.history.replaceState({}, '', url.toString())
}

// A redirect-based payment method leaves the page, and React state with it — but order submit on
// the return still wants the buyer's phone. The old Amazon Pay flow carried it in the return URL;
// a tab-scoped stash does the same job without writing it into the browser history.
const CHECKOUT_PHONE_STASH_KEY = 'gcg_checkout_phone'

// The email matters more than convenience: the redirect also loses the PHP session (the return
// arrives with an anonymous token), and order submit resolves the buyer by email when the session
// user is missing — the same fallback master's Amazon Pay flow fed by putting email in the return
// URL. A tab-scoped stash does the job without writing it into history or referrer headers.
const CHECKOUT_EMAIL_STASH_KEY = 'gcg_checkout_email'

// A Voucherify promo exists client-side only — the server cart never carries it and order submit
// applies it solely from the request payload — so it dies with the page on a redirect just like the
// phone. Stashed at confirm, it is exactly the code that was applied when the buyer saw the total;
// falling back to the raw promo cookie instead could send a code that never passed validation.
const CHECKOUT_PROMO_STASH_KEY = 'gcg_checkout_promo'

const stash = (key, value) => {
    try {
        sessionStorage.setItem(key, value || '')
    } catch (e) {
        // Storage can be unavailable (private mode); the order simply proceeds without the value.
    }
}

const readStash = (key) => {
    try {
        return sessionStorage.getItem(key) || ''
    } catch (e) {
        return ''
    }
}

export const stashCheckoutPhone = (phone) => stash(CHECKOUT_PHONE_STASH_KEY, phone)

export const readStashedCheckoutPhone = () => readStash(CHECKOUT_PHONE_STASH_KEY)

export const stashCheckoutEmail = (email) => stash(CHECKOUT_EMAIL_STASH_KEY, email)

export const readStashedCheckoutEmail = () => readStash(CHECKOUT_EMAIL_STASH_KEY)

export const stashCheckoutPromoCode = (promoCode) =>
    stash(CHECKOUT_PROMO_STASH_KEY, promoCode)

export const readStashedCheckoutPromoCode = () => readStash(CHECKOUT_PROMO_STASH_KEY)
